/**
 * 资源管理页面
 * 管理外部资源链接和配置
 */
import React, { useState, useEffect } from 'react';
import { LinkIcon, PlusIcon, PencilIcon, TrashIcon, GlobeAltIcon, DocumentIcon, ArrowPathIcon } from '@heroicons/react/24/outline';

interface Resource {
  id: string;
  name: string;
  url: string;
  description: string;
  type: 'api' | 'web' | 'document';
}

const ResourceManagement: React.FC = () => {
  const [loading, setLoading] = useState(true);

  // 模拟资源数据
  const mockResources: Resource[] = [
    {
      id: '1',
      name: '天眼查API',
      url: 'https://open.tianyancha.com',
      description: '企业信息查询接口',
      type: 'api',
    },
    {
      id: '2',
      name: '政府采购网',
      url: 'http://www.ccgp.gov.cn',
      description: '招标信息查询网站',
      type: 'web',
    },
    {
      id: '3',
      name: '行业标准文档',
      url: 'https://www.doc.com/standards',
      description: '相关行业标准文档库',
      type: 'document',
    },
  ];

  // 模拟加载
  useEffect(() => {
    const timer = setTimeout(() => setLoading(false), 500);
    return () => clearTimeout(timer);
  }, []);

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'api':
        return <LinkIcon className="w-6 h-6" />;
      case 'web':
        return <GlobeAltIcon className="w-6 h-6" />;
      case 'document':
        return <DocumentIcon className="w-6 h-6" />;
      default:
        return <LinkIcon className="w-6 h-6" />;
    }
  };

  const getTypeName = (type: string) => {
    switch (type) {
      case 'api':
        return 'API接口';
      case 'web':
        return '网站链接';
      case 'document':
        return '文档资源';
      default:
        return '未知';
    }
  };

  // 模拟加载
  useEffect(() => {
    const timer = setTimeout(() => setLoading(false), 500);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="h-full flex flex-col bg-transparent">
      {/* 头部 */}
      <div className="px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">资源管理</h1>
            <p className="text-sm text-gray-500 mt-1">管理外部资源链接和配置信息</p>
          </div>
          <button className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-xl shadow-lg shadow-blue-500/25 hover:shadow-blue-500/40 hover:scale-[1.02] active:scale-[0.98] transition-all duration-200 font-medium">
            <PlusIcon className="w-5 h-5" />
            添加资源
          </button>
        </div>
      </div>

      {/* 渐变分割线 */}
      <div className="mx-6 mt-2 h-px bg-gradient-to-r from-blue-400 via-indigo-500 to-blue-400 rounded-full" />

      {/* 资源列表 */}
      <div className="flex-1 overflow-y-auto p-6">
        {loading ? (
          <div className="flex flex-col items-center justify-center h-full text-gray-400">
            <ArrowPathIcon className="w-8 h-8 animate-spin mb-3" />
            <p className="text-base">加载中...</p>
          </div>
        ) : (
          <div className="space-y-3">
            {mockResources.map((resource) => (
            <div
              key={resource.id}
              className="bg-white rounded-lg border border-gray-200 p-5 hover:shadow-md transition-all"
            >
              <div className="flex items-start gap-4">
                <div className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${
                  resource.type === 'api' ? 'bg-blue-100 text-blue-600' :
                  resource.type === 'web' ? 'bg-green-100 text-green-600' :
                  'bg-purple-100 text-purple-600'
                }`}>
                  {getTypeIcon(resource.type)}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900 mb-1">{resource.name}</h3>
                      <span className="inline-block text-xs px-2 py-1 rounded bg-gray-100 text-gray-600">
                        {getTypeName(resource.type)}
                      </span>
                    </div>
                    <div className="flex gap-2 ml-4">
                      <button className="text-gray-400 hover:text-blue-600 transition-colors">
                        <PencilIcon className="w-5 h-5" />
                      </button>
                      <button className="text-gray-400 hover:text-red-600 transition-colors">
                        <TrashIcon className="w-5 h-5" />
                      </button>
                    </div>
                  </div>

                  <p className="text-sm text-gray-600 mb-3">{resource.description}</p>

                  <div className="flex items-center gap-2">
                    <code className="flex-1 text-xs bg-gray-50 px-3 py-2 rounded border border-gray-200 text-gray-700 truncate">
                      {resource.url}
                    </code>
                    <button
                      onClick={() => window.open(resource.url, '_blank')}
                      className="text-sm text-blue-600 hover:text-blue-700 font-medium whitespace-nowrap"
                    >
                      访问 →
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
        )}
      </div>
    </div>
  );
};

export default ResourceManagement;
